import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Hash, Users, SquarePen, Search, MessageCircle } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '../ui/tooltip';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';

const getInitials = (name = '') =>
  name.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase();

const stripHtml = (html) => {
  if (!html) return '';
  return html
    .replace(/<\/(?:p|div|li|br|h[1-6])>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

const formatLastMessage = (lastMessage) => {
  if (!lastMessage) return 'No messages yet';
  if (lastMessage.attachments?.length > 0) return `📎 ${lastMessage.attachments[0].fileName}`;
  const text = stripHtml(lastMessage.content);
  return text.length > 38 ? `${text.substring(0, 38)}…` : text;
};

const formatTime = (lastMessage) => {
  if (!lastMessage) return '';
  const d = new Date(lastMessage.createdAt);
  const now = new Date();
  const diffH = (now - d) / (1000 * 60 * 60);
  if (diffH < 24) return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const ConvItem = ({ conversation, isActive, onSelect, icon, iconBg }) => {
  const hasUnread = conversation.unreadCount > 0;
  return (
    <button
      onClick={() => {
        if (!conversation.id || !conversation.type) return;
        onSelect(conversation);
      }}
      className={cn(
        'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all duration-150',
        isActive
          ? 'bg-primary text-primary-foreground shadow-sm'
          : 'hover:bg-muted'
      )}
    >
      {icon ? (
        <div className={cn('w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0', isActive ? 'bg-white/20' : iconBg)}>
          <span className={isActive ? 'text-white' : ''}>{icon}</span>
        </div>
      ) : (
        <Avatar className="w-10 h-10 flex-shrink-0">
          {(conversation.profilePicture?.url || conversation.profilePicture?.data) && (
            <AvatarImage
              src={conversation.profilePicture.url || `data:${conversation.profilePicture.contentType};base64,${conversation.profilePicture.data}`}
              alt={conversation.name}
            />
          )}
          <AvatarFallback className={cn('text-sm font-semibold', isActive ? 'bg-white/20 text-white' : 'bg-primary/10 text-primary')}>
            {getInitials(conversation.name)}
          </AvatarFallback>
        </Avatar>
      )}

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <p className={cn('text-sm font-semibold truncate', isActive ? 'text-white' : 'text-foreground')}>
            {conversation.name}
          </p>
          <span className={cn('text-[11px] flex-shrink-0 ml-1', isActive ? 'text-white/60' : 'text-muted-foreground')}>
            {formatTime(conversation.lastMessage)}
          </span>
        </div>
        <div className="flex items-center justify-between mt-0.5">
          <p className={cn(
            'text-xs truncate flex-1',
            isActive ? 'text-white/75' : 'text-muted-foreground',
            hasUnread && !isActive && 'font-medium text-foreground'
          )}>
            {formatLastMessage(conversation.lastMessage)}
          </p>
          {hasUnread && (
            <span className={cn(
              'ml-2 flex-shrink-0 text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center leading-tight',
              isActive ? 'bg-white text-primary' : 'bg-primary text-white'
            )}>
              {conversation.unreadCount > 99 ? '99+' : conversation.unreadCount}
            </span>
          )}
        </div>
      </div>
    </button>
  );
};

const EmptyState = ({ icon: Icon, title, subtitle }) => (
  <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
    <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mb-3">
      <Icon className="w-7 h-7 text-muted-foreground" />
    </div>
    <p className="text-sm font-medium text-foreground">{title}</p>
    <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
  </div>
);

const MIN_WIDTH = 220;
const MAX_WIDTH = 480;

const ConversationsList = ({
  conversations,
  selectedConversation,
  onConversationSelect,
  showChat,
  onStartNewConversation,
  loading = false,
  availableUsersCount = 0
}) => {
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('groups');
  const [width, setWidth] = useState(288); // default 72 * 4 = 288px
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const onMouseDown = useCallback((e) => {
    isDragging.current = true;
    startX.current = e.clientX;
    startWidth.current = width;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [width]);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!isDragging.current) return;
      const delta = e.clientX - startX.current;
      const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth.current + delta));
      setWidth(newWidth);
    };
    const onMouseUp = () => {
      if (!isDragging.current) return;
      isDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const isActive = (conv) =>
    selectedConversation?.id === conv.id && selectedConversation?.type === conv.type;

  const groupConvs = conversations.filter(c => c.tag === 'Group');
  const customConvs = conversations.filter(c => c.tag === 'Custom');

  const filterFn = (list) =>
    search.trim() ? list.filter(c => c.name.toLowerCase().includes(search.toLowerCase())) : list;

  const totalUnread = (list) => list.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
  const groupUnread = totalUnread(groupConvs);
  const dmUnread = totalUnread(customConvs);

  const ConvList = ({ items, emptyIcon, emptyTitle, emptySubtitle, icon, iconBg }) => {
    const filtered = filterFn(items);
    if (filtered.length === 0) {
      return search.trim()
        ? <p className="text-center text-sm text-muted-foreground py-10">No results for "{search}"</p>
        : <EmptyState icon={emptyIcon} title={emptyTitle} subtitle={emptySubtitle} />;
    }
    return (
      <div className="space-y-0.5">
        {filtered.map(conv => (
          <ConvItem
            key={`${conv.type}-${conv.id}`}
            conversation={conv}
            isActive={isActive(conv)}
            onSelect={onConversationSelect}
            icon={icon}
            iconBg={iconBg}
          />
        ))}
      </div>
    );
  };

  return (
    <TooltipProvider>
      <div
        className={cn(
          'relative flex flex-col bg-background border-r border-border flex-shrink-0',
          showChat ? 'hidden md:flex' : 'flex'
        )}
        style={{ width: typeof window !== 'undefined' && window.innerWidth >= 768 ? width : '100%' }}
      >
        {/* Header */}
        <div className="px-4 pt-4 pb-3 flex items-center justify-between">
          <h2 className="text-lg font-bold text-foreground">Messages</h2>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button size="sm" onClick={() => onStartNewConversation?.()} className="gap-1.5 h-8">
                <SquarePen className="w-3.5 h-3.5" />
                New
                {availableUsersCount > 0 && (
                  <Badge variant="secondary" className="ml-0.5 px-1.5 py-0 text-[10px] h-4">
                    {availableUsersCount}
                  </Badge>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>Start a new conversation</TooltipContent>
          </Tooltip>
        </div>

        {/* Search */}
        <div className="px-3 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search conversations…"
              className="pl-9 bg-muted border-0 focus-visible:ring-1 h-9 text-sm"
            />
          </div>
        </div>

        <Separator />

        {/* Tabs are always mounted (controlled) so the active tab never resets on loading */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col flex-1 min-h-0">
          {/* Tab Bar */}
          <div className="px-3 pt-3 pb-1">
            <TabsList className="w-full grid grid-cols-2 h-9">
              <TabsTrigger value="groups" className="text-xs gap-1.5">
                <Hash className="w-3.5 h-3.5" />
                Groups
                {groupUnread > 0 && (
                  <span className="bg-primary text-primary-foreground text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center leading-tight">
                    {groupUnread > 99 ? '99+' : groupUnread}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="dms" className="text-xs gap-1.5">
                <Users className="w-3.5 h-3.5" />
                DMs
                {dmUnread > 0 && (
                  <span className="bg-primary text-primary-foreground text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center leading-tight">
                    {dmUnread > 99 ? '99+' : dmUnread}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="groups" className="flex-1 min-h-0 mt-0">
            {loading ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground py-16">
                <div className="w-7 h-7 border-2 border-primary border-t-transparent rounded-full animate-spin mb-3" />
                <p className="text-sm">Loading…</p>
              </div>
            ) : (
              <ScrollArea className="h-full">
                <div className="px-2 py-2">
                  <ConvList
                    items={groupConvs}
                    emptyIcon={Hash}
                    emptyTitle="No group chats"
                    emptySubtitle="You haven't joined any groups yet"
                    icon={<Hash className="w-4 h-4" />}
                    iconBg="bg-primary/10"
                  />
                </div>
              </ScrollArea>
            )}
          </TabsContent>

          <TabsContent value="dms" className="flex-1 min-h-0 mt-0">
            {loading ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground py-16">
                <div className="w-7 h-7 border-2 border-primary border-t-transparent rounded-full animate-spin mb-3" />
                <p className="text-sm">Loading…</p>
              </div>
            ) : (
              <ScrollArea className="h-full">
                <div className="px-2 py-2">
                  <ConvList
                    items={customConvs}
                    emptyIcon={MessageCircle}
                    emptyTitle="No direct messages"
                    emptySubtitle="Start a new conversation above"
                    icon={<Users className="w-4 h-4" />}
                    iconBg="bg-violet-100"
                  />
                </div>
              </ScrollArea>
            )}
          </TabsContent>
        </Tabs>

        {/* Drag handle */}
        <div
          onMouseDown={onMouseDown}
          className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize group hidden md:flex items-center justify-center z-10"
        >
          <div className="w-0.5 h-full bg-border group-hover:bg-primary/40 group-active:bg-primary transition-colors duration-150" />
        </div>
      </div>
    </TooltipProvider>
  );
};

export default ConversationsList;
